import {observer} from "mobx-react";
import React, {useMemo} from "react";

import {DataType} from "../../../stores/database-template/database-form-base";
import {bindInput} from "../../../utils/bind-input";
import {AddButton, SaveButton} from "../../atoms/buttons/buttons";
import {
    ActionsColumn,
    FieldsColumn,
    HeaderGrid,
    InputWidthWrapper,
    PageContainer,
} from "../../atoms/layouts/category-layout-containers";
import {FormField} from "../../molecules/inputs/form-field";
import {SubmitGroup} from "../../molecules/submit-group/submit-group";
import DatabaseRow from "../../molecules/tables/database-row";
import {AdminBreadcrumb} from "../../organisms/admin-breadcrumb/admin-breadcrumb";
import {DeleteDialog} from "../../organisms/delete-dialog/delete-dialog";
import {TableLayout} from "../../organisms/table-layout/table-layout";
import {AppLayout} from "../../templates/app-layout/app-layout";
import {DatabaseTemplateDetailEditPageStore} from "./database-template-detail-edit-store";

type Props = {pageStore: DatabaseTemplateDetailEditPageStore};

type Handlers = {
    onSlugChange: (id: string, value: string) => void;
    onLabelChange: (id: string, value: string) => void;
    onDataTypeChange: (id: string, value: DataType) => void;
    onDescriptionChange: (id: string, value: string) => void;
    onRemove: (id: string) => void;
};
// 1) ヘッダー部：Observable変化を監視
const TemplateHeader = observer(({store}: {store: DatabaseTemplateDetailEditPageStore}) => (
    <HeaderGrid>
        <FieldsColumn aria-label="テンプレート情報">
            <InputWidthWrapper>
                <FormField
                    label="テンプレート名"
                    required
                    value={store.templateName}
                    errorText={store.templateNameError}
                    placeholder="テンプレート名を入力"
                    {...bindInput(store.setTemplateName)}
                />
            </InputWidthWrapper>
            <InputWidthWrapper width={500}>
                <FormField
                    label="テンプレートの説明"
                    value={store.templateNote}
                    errorText={store.templateNoteError}
                    placeholder="テンプレートの説明を入力"
                    {...bindInput(store.setTemplateNote)}
                />
            </InputWidthWrapper>
        </FieldsColumn>
        <ActionsColumn>
            <AddButton onClick={() => store.addTemplateRow()}>項目を追加</AddButton>
            <SaveButton disabled={!store.canUpdate} onClick={store.updateTemplate}>
                保存
            </SaveButton>
        </ActionsColumn>
    </HeaderGrid>
));

// 2) テーブル：Observable変化を監視
const TemplateEditTable = observer(
    ({store, handlers}: {store: DatabaseTemplateDetailEditPageStore; handlers: Handlers}) => (
        <TableLayout areaLabel="テンプレート編集" columns={store.DEFAULT_COLUMN_DEFS}>
            {store.templateColumnList
                .filter((r) => !r.deleted)
                .map((row) => (
                    <DatabaseRow
                        key={row.id}
                        isEdit
                        {...row}
                        {...handlers}
                        canRemove={store.templateColumnList.filter((r2) => !r2.deleted).length > 1}
                    />
                ))}
        </TableLayout>
    ),
);

// 3) ページ本体は observer を外し、必要な部分だけ再レンダーさせる
const DatabaseTemplateDetailEditPage: React.FC<Props> = ({pageStore}) => {
    const handlers = useMemo<Handlers>(
        () => ({
            onSlugChange: pageStore.setSlug,
            onLabelChange: pageStore.setLabel,
            onDataTypeChange: pageStore.setDataTypeSelect,
            onDescriptionChange: pageStore.setNote,
            onRemove: pageStore.removeTemplateRow,
        }),
        [pageStore],
    );

    return (
        <AppLayout
            breadcrumb={
                <AdminBreadcrumb
                    factory={(b) =>
                        b
                            .databaseTemplates()
                            .databaseTemplateDetail(pageStore.templateName, pageStore.templateId)
                            .custom("編集")
                            .build()
                    }
                />
            }
        >
            <SubmitGroup
                onSubmit={() => pageStore.updateTemplate()}
                canSubmit={pageStore.canUpdate}
            >
                <PageContainer>
                    <TemplateHeader store={pageStore} />
                    <TemplateEditTable store={pageStore} handlers={handlers} />
                </PageContainer>
            </SubmitGroup>
            <DeleteDialog />
        </AppLayout>
    );
};

export default DatabaseTemplateDetailEditPage;
